import { addBreadcrumb } from '@sentry/react';
import { AxiosRequestConfig } from 'axios';
import sha256 from 'crypto-js/sha256';
import i18next from 'i18next';
import { flatten, sortBy } from 'lodash';

import ApiClient, { ApiOptions } from 'lib/clients/ApiClient';
import StorageClient, { SessionStorageKey } from 'lib/clients/StorageClient';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { jwtDecode } from 'lib/jwt';
import { Json } from 'lib/types';
import { base64EncodeUnicode, flattenRequestBody } from 'lib/utils/checksum';
import { HolderClient } from 'verify/lib/clients/HolderClient';

export const API_ROOT_VERIFY = '/api/internal/verify/v1';

// See app/lib/api/internal/verify/version.rb for all versions.
const API_VERSION = '2023-08-08';

export enum InquirySessionError {
  Other = 'other',
  Network = 'network',
  Camera = 'camera',
  Permissions = 'permissions',
}

class Client extends ApiClient {
  private myAccessToken?: string;
  // Used to set Persona-Country header to route requests to our servers in that country
  private countryToRouteRequests?: string;

  // For request bucketing / analysis
  private organizationMetadata: {
    inquiryTemplateId?: string;
    organizationId?: string;
    environmentId?: string;
  } = {};

  public get accessToken(): string | undefined {
    // local / session storage misses return null and not undefined. This just makes the return value consistent.
    return (
      this.myAccessToken ??
      StorageClient.getSessionItem(SessionStorageKey.InquiryAccessToken) ??
      undefined
    );
  }

  public set accessToken(accessToken) {
    if (jwtDecode(accessToken) == null) {
      // Token is not a properly formatted JWT; ignore it
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.myAccessToken = null;
      StorageClient.removeSessionItem(SessionStorageKey.InquiryAccessToken);
      HolderClient.removeSessionTokenFromCachedParams();
    } else {
      this.myAccessToken = accessToken;

      accessToken == null
        ? StorageClient.removeSessionItem(SessionStorageKey.InquiryAccessToken)
        : StorageClient.setSessionItem(SessionStorageKey.InquiryAccessToken, accessToken);
    }
  }

  public clearAccessToken(): void {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.accessToken = null;
  }

  public getAuthHeaders() {
    if (this.accessToken) {
      return { Authorization: `Bearer ${this.accessToken}` };
    }
  }

  public getOrganizationMetadataHeaders() {
    return {
      'Persona-Inquiry-Template-Id': this.organizationMetadata.inquiryTemplateId,
      'Persona-Organization-Id': this.organizationMetadata.organizationId,
      'Persona-Environment-Id': this.organizationMetadata.environmentId,
    };
  }

  public getSdkVersionHeaders() {
    if (HolderClient.inquiryParams['client-version'] != null) {
      return {
        'Persona-Js-Sdk-Version': HolderClient.inquiryParams['client-version'],
      };
    } else {
      return {};
    }
  }

  public getChecksumHeaders(currentTimestamp: number, requestBody: any) {
    if (this.accessToken) {
      const nhmjlnrs = Math.floor(currentTimestamp / 1000).toString();

      // Flatten request params to an array, converting files to name/size,
      // and then sorting it. This generates signatures in a stable way and
      // is significantly faster than reading the entire file into memory.
      let encodedRequestBody = flattenRequestBody(requestBody)
        .map((part) => {
          if (part[1] instanceof Blob) {
            return [part[0], String(part[1].size)];
          } else if (/^[+-]?\d+(\.\d+)?$/.test(String(part[1]))) {
            return [part[0], String(part[1]).substring(0, 10)];
          } else {
            return [part[0], String(part[1]).replace(/\s/g, '')];
          }
        })
        .filter((part) => part[1].length > 0);
      encodedRequestBody = sortBy(encodedRequestBody, [
        (part) => {
          return flatten<string | number>(part).join();
        },
      ]);

      const stpbwsbb = sha256(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        [jwtDecode(this.accessToken).sub, nhmjlnrs, JSON.stringify(encodedRequestBody)].join(':'),
      ).toString();

      return {
        nhmjlnrs: nhmjlnrs,
        stpbwsbb: stpbwsbb,
        // This is a purely informational header for debugging.
        // We truncate to the first 1000 characters and then base64 encode to avoid sending invalid headers.
        requestparams: base64EncodeUnicode(
          JSON.stringify(encodedRequestBody.map((part) => part[1])).slice(0, 1000),
        ),
      };
    }
  }

  public get styleVariant(): 'base' | 'dark' | string {
    if (HolderClient.inquiryParams['style-variant']) {
      return HolderClient.inquiryParams['style-variant'];
    }

    if (window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
      return 'dark';
    }

    return 'base';
  }

  public getHeaders() {
    return {
      ...this.getAuthHeaders(),
      ...this.getOrganizationMetadataHeaders(),
      ...this.getSdkVersionHeaders(),
      'Persona-Version': API_VERSION,
      'Persona-Device-Id':
        StorageClient.getSessionItem(SessionStorageKey.InquiryDeviceId) ?? undefined,
      'Persona-Device-Locale': i18next.language,
      'Persona-Style-Variant': this.styleVariant,
      'Persona-Country':
        this.countryToRouteRequests ?? HolderClient.inquiryParams['routing-country'],
    };
  }

  public onError = async (err: { type: InquirySessionError; description?: Json }) => {
    // TODO: Figure out why SessionClient.postResourceSingletonAction isn't working,
    // seems to be json api content-type header related?
    return fetch(`${API_ROOT_VERIFY}/current-inquiry-session/error`, {
      method: 'post',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      headers: { ...this.getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error_type: err.type,
        debug_description: err.description || {},
      }),
    });
  };

  public onCancel = async () => {
    return fetch(`${API_ROOT_VERIFY}/current-inquiry-session/cancel`, {
      method: 'post',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      headers: this.getHeaders(),

      // https://css-tricks.com/send-an-http-request-on-page-exit/
      keepalive: true,
    });
  };

  // Use country to determine what FPJS token to use for fingerprinting
  // see front-end/lib/utils/browserFingerprint.ts
  public get fpjsCountry(): string {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return this.countryToRouteRequests;
  }

  protected async sendRequest(
    uri: string,
    requestOptions: AxiosRequestConfig,
    options: ApiOptions = {},
  ) {
    const requestOptionsWithHeaders = {
      ...requestOptions,
      headers: {
        ...requestOptions.headers,
        ...this.getHeaders(),
        ...this.getChecksumHeaders(Date.now(), requestOptions.data),
      },
    };

    try {
      const response = await super.sendRequest(uri, requestOptionsWithHeaders, options);

      // NOTE: Axios coerces all response headers to be lowercase
      if (response?.headers?.['persona-device-id']) {
        StorageClient.setSessionItem(
          SessionStorageKey.InquiryDeviceId,
          response.headers['persona-device-id'],
        );
      }

      // Send subsequent requests to servers in country determined by backend for this inquiry flow.
      if (response?.headers?.['persona-country']) {
        this.countryToRouteRequests = response.headers['persona-country'];
      }

      if (response?.headers?.['persona-organization-id']) {
        this.organizationMetadata.organizationId = response.headers['persona-organization-id'];
      }
      if (response?.headers?.['persona-environment-id']) {
        this.organizationMetadata.environmentId = response.headers['persona-environment-id'];
      }
      if (response?.headers?.['persona-inquiry-template-id']) {
        this.organizationMetadata.inquiryTemplateId =
          response.headers['persona-inquiry-template-id'];
      }

      return response;
    } catch (err) {
      // In /verify, we assume all 401s to be expired sessions and reset.
      // Depending on the type of session, we may want to reset in different ways in the future.
      if (err instanceof ApiResponseError && err.status === 401) {
        addBreadcrumb({
          level: 'warning',
          message: 'Failed to authenticate',
          data: {
            name: err.name,
            method: err.method,
            uri: err.uri,
            errors: err.errors,
          },
        });
        // If an expired token persists in session storage it can cause
        // repeated 401s even when a session token is not required.
        //
        // FIXME: [PER-23269] This often causes downstream code to error,
        // because the request resolves with an undefined response. Subsequent
        // code that accesses the response will raise NPE.
        // We should raise an error here that gets silently handled to skip any
        // subsequent code that depends on responses.
        this.clearAccessToken();

        // Also delete from the query string or else we will repeatedly redirect to the
        // start with the expired token
        delete HolderClient.inquiryParams['session-token'];
        HolderClient.redirectToStart();
        // Mainly to help complete the caller's code path
        throw err;
      } else {
        throw err;
      }
    }
  }
}

export default new Client(API_ROOT_VERIFY);
